import type { ExplorerFinding } from '../../../../models/explorer/ExplorerFinding';
import type { FindingRule, FindingRuleContext } from '../types';
import { propsOf } from './_graphHelpers';

const DEFAULT_REQUIRED_TAGS = ['Env', 'Owner', 'CostCenter'] as const;

/**
 * P0.3 — untagged resource (missing one or more required tags).
 * Severity: info. SOC 2 CC2.1 / hygiene.
 *
 * Required-tag list comes from folder settings when present; defaults
 * otherwise (per impl plan §P0.3).
 */
export const untaggedResourceRule: FindingRule = {
  id: 'untagged_resource',
  label: 'Untagged resource',
  description: 'Resource missing one or more organisation-required tags.',

  run(ctx: FindingRuleContext): readonly ExplorerFinding[] {
    const required = (ctx.folderSettings?.requiredTags ?? DEFAULT_REQUIRED_TAGS).map(
      (t) => t.toLowerCase(),
    );
    if (required.length === 0) return [];
    const findings: ExplorerFinding[] = [];
    for (const node of ctx.graph.nodes) {
      // Skip synthetic / container entries; require a tags map to exist
      // at all before we flag. Absent tags map → unknown not missing.
      const p = propsOf(node);
      const tags = p.tags as Record<string, string> | undefined;
      if (!tags) continue;
      const tagKeysLower = Object.keys(tags).map((k) => k.toLowerCase());
      const missing = required.filter((req) => !tagKeysLower.includes(req));
      if (missing.length === 0) continue;
      findings.push({
        id: `untagged_resource:resource:${node.id}`,
        ruleId: 'untagged_resource',
        severity: 'info',
        scope: 'resource',
        subject: { kind: 'resource', id: node.id },
        title: 'Untagged resource',
        detail: `${String(node.label ?? node.id)} missing required tag(s): ${missing.join(', ')}.`,
        attrs: { missing },
      });
    }
    return findings;
  },
};
